// Supported cities — each maps to a university_id and/or city filter for backend queries.
// To add a new city, add an entry here and it propagates site-wide.

export interface SupportedCity {
    id: string;           // unique slug, e.g. 'tampa-fl'
    label: string;        // display label, e.g. 'Tampa, FL'
    universityId: number; // unique negative ID for UI city picks
    universityName: string;
    aliases?: string[];
    /** When set, listings are fetched via city= query (metro hubs with tagged listings). */
    cityFilter?: string;
    /** When set (and no cityFilter), listings are fetched via university_id=. */
    listingUniversityId?: number;
    /** Optional map center for city search results. */
    mapCenter?: { longitude: number; latitude: number; zoom: number };
    mapPin?: { longitude: number; latitude: number };
    mapPinUniversityName?: string;
    mapPinUniversityAbbr?: string;
}

export const SUPPORTED_CITIES: SupportedCity[] = [
    // ——— Florida ———
    {
        id: 'tampa-fl',
        label: 'Tampa, FL',
        universityId: -10,
        universityName: 'Tampa, FL',
        cityFilter: 'Tampa',
        listingUniversityId: 10,
        aliases: ['tampa', 'tampa fl', 'tampa bay'],
        mapCenter: { longitude: -82.4572, latitude: 27.9506, zoom: 10 },
        mapPin: { longitude: -82.4138, latitude: 28.0587 },
        mapPinUniversityName: 'University of South Florida',
        mapPinUniversityAbbr: 'USF',
    },
    {
        id: 'st-petersburg-fl',
        label: 'St. Petersburg, FL',
        universityId: -1001,
        universityName: 'St. Petersburg, FL',
        cityFilter: 'Tampa',
        listingUniversityId: 10,
        aliases: ['st petersburg', 'saint petersburg', 'st pete'],
    },
    {
        id: 'clearwater-fl',
        label: 'Clearwater, FL',
        universityId: -1002,
        universityName: 'Clearwater, FL',
        cityFilter: 'Tampa',
        listingUniversityId: 10,
        aliases: ['clearwater'],
    },
    {
        id: 'miami-fl',
        label: 'Miami, FL',
        universityId: -4,
        universityName: 'Miami, FL',
        listingUniversityId: 4,
        aliases: ['miami', 'miami fl', 'miami beach'],
        mapCenter: { longitude: -80.1918, latitude: 25.7617, zoom: 11 },
        mapPin: { longitude: -80.3739, latitude: 25.7571 },
        mapPinUniversityName: 'Florida International University',
        mapPinUniversityAbbr: 'FIU',
    },
    {
        id: 'coral-gables-fl',
        label: 'Coral Gables, FL',
        universityId: -8,
        universityName: 'Coral Gables, FL',
        listingUniversityId: 8,
        aliases: ['coral gables', 'university of miami'],
    },
    {
        id: 'orlando-fl',
        label: 'Orlando, FL',
        universityId: -6,
        universityName: 'Orlando, FL',
        listingUniversityId: 6,
        aliases: ['orlando', 'orlando fl'],
        mapCenter: { longitude: -81.3792, latitude: 28.5383, zoom: 11 },
        mapPin: { longitude: -81.2001, latitude: 28.6024 },
        mapPinUniversityName: 'University of Central Florida',
        mapPinUniversityAbbr: 'UCF',
    },
    {
        id: 'jacksonville-fl',
        label: 'Jacksonville, FL',
        universityId: -9,
        universityName: 'Jacksonville, FL',
        listingUniversityId: 9,
        aliases: ['jacksonville', 'jacksonville fl', 'jax'],
    },
    {
        id: 'tallahassee-fl',
        label: 'Tallahassee, FL',
        universityId: -5,
        universityName: 'Tallahassee, FL',
        listingUniversityId: 5,
        aliases: ['tallahassee', 'tally'],
        mapCenter: { longitude: -84.2807, latitude: 30.4383, zoom: 11 },
        mapPin: { longitude: -84.3046, latitude: 30.4419 },
        mapPinUniversityName: 'Florida State University',
        mapPinUniversityAbbr: 'FSU',
    },
    {
        id: 'gainesville-fl',
        label: 'Gainesville, FL',
        universityId: -7,
        universityName: 'Gainesville, FL',
        listingUniversityId: 7,
        aliases: ['gainesville'],
        mapCenter: { longitude: -82.3248, latitude: 29.6516, zoom: 12 },
        mapPin: { longitude: -82.3431, latitude: 29.6436 },
        mapPinUniversityName: 'University of Florida',
        mapPinUniversityAbbr: 'UF',
    },
    {
        id: 'fort-lauderdale-fl',
        label: 'Fort Lauderdale, FL',
        universityId: -2001,
        universityName: 'Fort Lauderdale, FL',
        listingUniversityId: 2,
        aliases: ['fort lauderdale', 'ft lauderdale', 'ft. lauderdale'],
    },
    {
        id: 'boca-raton-fl',
        label: 'Boca Raton, FL',
        universityId: -2,
        universityName: 'Boca Raton, FL',
        listingUniversityId: 2,
        aliases: ['boca raton', 'boca'],
    },
    {
        id: 'west-palm-beach-fl',
        label: 'West Palm Beach, FL',
        universityId: -2002,
        universityName: 'West Palm Beach, FL',
        listingUniversityId: 2,
        aliases: ['west palm beach', 'west palm', 'wpb'],
    },
    {
        id: 'fort-myers-fl',
        label: 'Fort Myers, FL',
        universityId: -3,
        universityName: 'Fort Myers, FL',
        listingUniversityId: 3,
        aliases: ['fort myers', 'ft myers', 'cape coral'],
    },
    {
        id: 'naples-fl',
        label: 'Naples, FL',
        universityId: -3001,
        universityName: 'Naples, FL',
        listingUniversityId: 3,
        aliases: ['naples'],
    },
    {
        id: 'pensacola-fl',
        label: 'Pensacola, FL',
        universityId: -11,
        universityName: 'Pensacola, FL',
        listingUniversityId: 11,
        aliases: ['pensacola'],
    },
    {
        id: 'lakeland-fl',
        label: 'Lakeland, FL',
        universityId: -12,
        universityName: 'Lakeland, FL',
        listingUniversityId: 12,
        aliases: ['lakeland'],
    },
    {
        id: 'daytona-beach-fl',
        label: 'Daytona Beach, FL',
        universityId: -14,
        universityName: 'Daytona Beach, FL',
        listingUniversityId: 14,
        aliases: ['daytona beach', 'daytona'],
    },
    {
        id: 'melbourne-fl',
        label: 'Melbourne, FL',
        universityId: -15,
        universityName: 'Melbourne, FL',
        listingUniversityId: 15,
        aliases: ['melbourne'],
    },
    {
        id: 'sarasota-fl',
        label: 'Sarasota, FL',
        universityId: -19,
        universityName: 'Sarasota, FL',
        listingUniversityId: 19,
        aliases: ['sarasota'],
    },
    {
        id: 'winter-park-fl',
        label: 'Winter Park, FL',
        universityId: -17,
        universityName: 'Winter Park, FL',
        listingUniversityId: 17,
        aliases: ['winter park'],
    },
    {
        id: 'deland-fl',
        label: 'DeLand, FL',
        universityId: -18,
        universityName: 'DeLand, FL',
        listingUniversityId: 18,
        aliases: ['deland', 'de land'],
    },
    {
        id: 'davie-fl',
        label: 'Davie, FL',
        universityId: -13,
        universityName: 'Davie, FL',
        listingUniversityId: 13,
        aliases: ['davie', 'nova southeastern'],
    },
    {
        id: 'miami-shores-fl',
        label: 'Miami Shores, FL',
        universityId: -20,
        universityName: 'Miami Shores, FL',
        listingUniversityId: 20,
        aliases: ['miami shores', 'barry'],
    },

    // ——— New York ———
    {
        id: 'nyc-ny',
        label: 'New York City, NY',
        universityId: -123,
        universityName: 'New York City, NY',
        cityFilter: 'New York City',
        listingUniversityId: 123,
        aliases: ['nyc', 'new york', 'new york city', 'new york city ny', 'manhattan'],
        mapCenter: { longitude: -74.0060, latitude: 40.7128, zoom: 11 },
        mapPin: { longitude: -73.9965, latitude: 40.7295 },
        mapPinUniversityName: 'New York University',
        mapPinUniversityAbbr: 'NYU',
    },
    {
        id: 'brooklyn-ny',
        label: 'Brooklyn, NY',
        universityId: -1231,
        universityName: 'Brooklyn, NY',
        cityFilter: 'New York City',
        listingUniversityId: 123,
        aliases: ['brooklyn'],
    },
    {
        id: 'queens-ny',
        label: 'Queens, NY',
        universityId: -1232,
        universityName: 'Queens, NY',
        cityFilter: 'New York City',
        listingUniversityId: 123,
        aliases: ['queens', 'flushing', 'astoria', 'long island city'],
    },
    {
        id: 'bronx-ny',
        label: 'Bronx, NY',
        universityId: -1233,
        universityName: 'Bronx, NY',
        cityFilter: 'New York City',
        listingUniversityId: 123,
        aliases: ['bronx', 'the bronx'],
    },
    {
        id: 'buffalo-ny',
        label: 'Buffalo, NY',
        universityId: -184,
        universityName: 'Buffalo, NY',
        listingUniversityId: 184,
        aliases: ['buffalo'],
        mapCenter: { longitude: -78.8784, latitude: 42.8864, zoom: 11 },
    },
    {
        id: 'rochester-ny',
        label: 'Rochester, NY',
        universityId: -131,
        universityName: 'Rochester, NY',
        listingUniversityId: 131,
        aliases: ['rochester'],
    },
    {
        id: 'syracuse-ny',
        label: 'Syracuse, NY',
        universityId: -136,
        universityName: 'Syracuse, NY',
        listingUniversityId: 136,
        aliases: ['syracuse'],
    },
    {
        id: 'albany-ny',
        label: 'Albany, NY',
        universityId: -186,
        universityName: 'Albany, NY',
        listingUniversityId: 186,
        aliases: ['albany'],
    },
    {
        id: 'ithaca-ny',
        label: 'Ithaca, NY',
        universityId: -110,
        universityName: 'Ithaca, NY',
        listingUniversityId: 110,
        aliases: ['ithaca'],
    },
    {
        id: 'binghamton-ny',
        label: 'Binghamton, NY',
        universityId: -185,
        universityName: 'Binghamton, NY',
        listingUniversityId: 185,
        aliases: ['binghamton'],
    },
    {
        id: 'stony-brook-ny',
        label: 'Stony Brook, NY',
        universityId: -183,
        universityName: 'Stony Brook, NY',
        listingUniversityId: 183,
        aliases: ['stony brook'],
    },
    {
        id: 'hempstead-ny',
        label: 'Hempstead, NY',
        universityId: -187,
        universityName: 'Hempstead, NY',
        listingUniversityId: 187,
        aliases: ['hempstead', 'hofstra', 'long island'],
    },
    {
        id: 'troy-ny',
        label: 'Troy, NY',
        universityId: -196,
        universityName: 'Troy, NY',
        listingUniversityId: 196,
        aliases: ['troy', 'rpi'],
    },
    {
        id: 'poughkeepsie-ny',
        label: 'Poughkeepsie, NY',
        universityId: -198,
        universityName: 'Poughkeepsie, NY',
        listingUniversityId: 198,
        aliases: ['poughkeepsie', 'vassar'],
    },
    {
        id: 'hamilton-ny',
        label: 'Hamilton, NY',
        universityId: -197,
        universityName: 'Hamilton, NY',
        listingUniversityId: 197,
        aliases: ['hamilton', 'colgate'],
    },
    {
        id: 'saratoga-springs-ny',
        label: 'Saratoga Springs, NY',
        universityId: -200,
        universityName: 'Saratoga Springs, NY',
        listingUniversityId: 200,
        aliases: ['saratoga springs', 'skidmore'],
    },

    // ——— Surrounding (NJ / CT) ———
    {
        id: 'new-brunswick-nj',
        label: 'New Brunswick, NJ',
        universityId: -132,
        universityName: 'New Brunswick, NJ',
        listingUniversityId: 132,
        aliases: ['new brunswick', 'rutgers'],
    },
    {
        id: 'princeton-nj',
        label: 'Princeton, NJ',
        universityId: -212,
        universityName: 'Princeton, NJ',
        listingUniversityId: 212,
        aliases: ['princeton'],
    },
    {
        id: 'hoboken-nj',
        label: 'Hoboken, NJ',
        universityId: -211,
        universityName: 'Hoboken, NJ',
        listingUniversityId: 211,
        aliases: ['hoboken', 'stevens'],
    },
    {
        id: 'newark-nj',
        label: 'Newark, NJ',
        universityId: -213,
        universityName: 'Newark, NJ',
        listingUniversityId: 213,
        aliases: ['newark'],
    },
    {
        id: 'south-orange-nj',
        label: 'South Orange, NJ',
        universityId: -214,
        universityName: 'South Orange, NJ',
        listingUniversityId: 214,
        aliases: ['south orange', 'seton hall'],
    },
    {
        id: 'new-haven-ct',
        label: 'New Haven, CT',
        universityId: -215,
        universityName: 'New Haven, CT',
        listingUniversityId: 215,
        aliases: ['new haven', 'yale'],
    },
    {
        id: 'storrs-ct',
        label: 'Storrs, CT',
        universityId: -150,
        universityName: 'Storrs, CT',
        listingUniversityId: 150,
        aliases: ['storrs', 'uconn'],
    },
    {
        id: 'fairfield-ct',
        label: 'Fairfield, CT',
        universityId: -216,
        universityName: 'Fairfield, CT',
        listingUniversityId: 216,
        aliases: ['fairfield'],
    },
];

/** Searchable city for dropdown suggestions (supported + unsupported majors). */
export interface SearchableCity {
    id: string;
    label: string;
    aliases?: string[];
    /** Present only when the city is supported for search. */
    universityId?: number;
    supported: boolean;
}

/**
 * Major US cities shown in home search suggestions.
 * Supported cities map to backend IDs; others trigger the vote flow.
 */
export const MAJOR_US_CITIES: SearchableCity[] = [
    // Florida + NY (+ surrounding) — all supported
    ...SUPPORTED_CITIES.map((city) => ({
        id: city.id,
        label: city.label,
        universityId: city.universityId,
        supported: true,
        aliases: city.aliases,
    })),
    // Other major metros (vote flow)
    { id: 'los-angeles-ca', label: 'Los Angeles, CA', supported: false, aliases: ['la', 'los angeles'] },
    { id: 'san-francisco-ca', label: 'San Francisco, CA', supported: false, aliases: ['sf', 'san francisco'] },
    { id: 'san-diego-ca', label: 'San Diego, CA', supported: false },
    { id: 'san-jose-ca', label: 'San Jose, CA', supported: false },
    { id: 'sacramento-ca', label: 'Sacramento, CA', supported: false },
    { id: 'chicago-il', label: 'Chicago, IL', supported: false },
    { id: 'houston-tx', label: 'Houston, TX', supported: false },
    { id: 'dallas-tx', label: 'Dallas, TX', supported: false },
    { id: 'austin-tx', label: 'Austin, TX', supported: false },
    { id: 'san-antonio-tx', label: 'San Antonio, TX', supported: false },
    { id: 'philadelphia-pa', label: 'Philadelphia, PA', supported: false, aliases: ['philly'] },
    { id: 'phoenix-az', label: 'Phoenix, AZ', supported: false },
    { id: 'boston-ma', label: 'Boston, MA', supported: false },
    { id: 'seattle-wa', label: 'Seattle, WA', supported: false },
    { id: 'denver-co', label: 'Denver, CO', supported: false },
    { id: 'atlanta-ga', label: 'Atlanta, GA', supported: false },
    { id: 'washington-dc', label: 'Washington, DC', supported: false, aliases: ['dc', 'washington dc'] },
    { id: 'nashville-tn', label: 'Nashville, TN', supported: false },
    { id: 'charlotte-nc', label: 'Charlotte, NC', supported: false },
    { id: 'raleigh-nc', label: 'Raleigh, NC', supported: false },
    { id: 'detroit-mi', label: 'Detroit, MI', supported: false },
    { id: 'minneapolis-mn', label: 'Minneapolis, MN', supported: false },
    { id: 'portland-or', label: 'Portland, OR', supported: false },
    { id: 'las-vegas-nv', label: 'Las Vegas, NV', supported: false },
    { id: 'salt-lake-city-ut', label: 'Salt Lake City, UT', supported: false },
    { id: 'kansas-city-mo', label: 'Kansas City, MO', supported: false },
    { id: 'columbus-oh', label: 'Columbus, OH', supported: false },
    { id: 'indianapolis-in', label: 'Indianapolis, IN', supported: false },
    { id: 'pittsburgh-pa', label: 'Pittsburgh, PA', supported: false },
    { id: 'baltimore-md', label: 'Baltimore, MD', supported: false },
    { id: 'new-orleans-la', label: 'New Orleans, LA', supported: false },
];

/** Given a university_id (negative city pick), return the matching city (if any). */
export const getCityByUniversityId = (universityId: number): SupportedCity | undefined =>
    SUPPORTED_CITIES.find(c => c.universityId === universityId);

/** Resolve listing university ID for a supported city. */
export function getCityListingUniversityId(city: SupportedCity): number {
    if (city.listingUniversityId != null) return city.listingUniversityId;
    if (city.cityFilter === 'Tampa') return 10;
    if (city.cityFilter === 'New York City') return 123;
    return Math.abs(city.universityId);
}

/** City picks use negative pseudo-IDs in the UI; the API expects positive university IDs (≥ 1). */
export function resolveBackendUniversityId(universityId: number): number | undefined {
    if (!Number.isFinite(universityId)) {
        return undefined;
    }
    if (universityId >= 1) {
        return universityId;
    }
    const city = getCityByUniversityId(universityId);
    if (city) {
        return getCityListingUniversityId(city);
    }
    return undefined;
}

/** Given a city id slug, return the matching city (if any). */
export const getCityById = (id: string): SupportedCity | undefined =>
    SUPPORTED_CITIES.find(c => c.id === id);

/** Find supported city whose cityFilter matches a URL/API city name. */
export const getCityByFilterName = (cityName: string): SupportedCity | undefined => {
    const normalized = cityName.trim().toLowerCase();
    return SUPPORTED_CITIES.find(
        (c) => c.cityFilter && c.cityFilter.toLowerCase() === normalized
            && (c.id === 'tampa-fl' || c.id === 'nyc-ny')
    ) || SUPPORTED_CITIES.find(
        (c) => c.cityFilter && c.cityFilter.toLowerCase() === normalized
    );
};

const normalizeCityText = (value: string) =>
    value.toLowerCase().replace(/[^a-z0-9\s]/g, '').replace(/\s+/g, ' ').trim();

/** Match a typed query to a supported city (exact / alias). */
export function matchSupportedCityFromInput(input: string): SupportedCity | undefined {
    const normalized = normalizeCityText(input);
    if (!normalized) return undefined;

    return SUPPORTED_CITIES.find((city) => {
        const labelNorm = normalizeCityText(city.label);
        const labelWithoutState = labelNorm.replace(/\s+(fl|ny|nj|ct)$/, '').trim();
        const aliases = (city.aliases || []).map(normalizeCityText);
        return (
            labelNorm === normalized ||
            labelWithoutState === normalized ||
            aliases.some((a) => a === normalized)
        );
    });
}

/** Append city= or university_id= to a URLSearchParams based on the selected location ID. */
export function appendListingLocationParams(
    params: URLSearchParams,
    selectedId: string | number,
): void {
    const idNum = typeof selectedId === 'number' ? selectedId : parseInt(String(selectedId), 10);
    if (Number.isNaN(idNum)) return;

    // State picks (-5001..-5004) intentionally omit location filters; Home client-filters by uni IDs.
    if (idNum <= -5001 && idNum >= -5099) {
        return;
    }

    const city = getCityByUniversityId(idNum);
    if (city?.cityFilter) {
        params.set('city', city.cityFilter);
        return;
    }
    if (city) {
        params.set('university_id', String(getCityListingUniversityId(city)));
        return;
    }
    if (idNum > 0) {
        params.set('university_id', String(idNum));
    }
}

/** Match major cities against a typed query. Empty query returns nothing. */
export const matchMajorCities = (query: string): SearchableCity[] => {
    const q = normalizeCityText(query);
    if (!q) return [];

    const normalizedQueryCompact = q.replace(/\s+/g, '');

    return MAJOR_US_CITIES.filter((city) => {
        const labelNorm = normalizeCityText(city.label);
        const labelCompact = labelNorm.replace(/\s+/g, '');
        const idNorm = city.id.replace(/-/g, ' ');
        const aliases = (city.aliases || []).map(normalizeCityText);

        if (labelNorm.startsWith(q) || labelNorm.includes(q)) return true;
        if (labelCompact.includes(normalizedQueryCompact)) return true;
        if (idNorm.includes(q)) return true;
        if (aliases.some((a) => a === q || a.startsWith(q) || a.includes(q))) return true;

        const queryWords = q.split(/\s+/);
        const haystack = `${labelNorm} ${idNorm} ${aliases.join(' ')}`;
        if (queryWords.every((word) => haystack.includes(word))) return true;

        return false;
    });
};
